import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Send, Check, X, Download, Eye, RefreshCw, FileText, Search } from "lucide-react";
import { useRowHighlight } from "@/lib/useRowHighlight";

const ESTADOS = {
  borrador: { label: "Borrador", cls: "bg-[#e6ebe8] text-[#5c7368]", step: 0 },
  enviado: { label: "Enviado", cls: "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]", step: 1 },
  requiere_correccion: { label: "Requiere Correcciones", cls: "bg-[#fef3e7] text-[#a5560c] border border-[#f0b874]", step: 0 },
  aprobado: { label: "Aprobado", cls: "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]", step: 2 },
  rechazado: { label: "Rechazado", cls: "bg-[#fdecea] text-[#b53a25] border border-[#d99584]", step: -1 },
  reembolsado: { label: "Reembolsado", cls: "bg-[#205c45] text-white", step: 3 },
};

const ROLES_VEN_TODAS = ["admin", "gerencia", "contabilidad", "rrhh"];

// Timeline horizontal de pasos
function Timeline({ estado }) {
  const steps = [
    { key: "borrador", label: "Borrador" },
    { key: "enviado", label: "Enviado" },
    { key: "aprobado", label: "Aprobado" },
    { key: "reembolsado", label: "Reembolsado" },
  ];
  const current = ESTADOS[estado]?.step ?? 0;
  const isRech = estado === "rechazado";
  const isCorr = estado === "requiere_correccion";
  return (
    <div className="flex items-center gap-1 w-full" data-testid="liqv-timeline">
      <input type="hidden" data-testid={`liqv-timeline-state-${estado}`} />
      {steps.map((s, i) => {
        const done = !isRech && !isCorr && current >= i;
        const active = !isRech && !isCorr && current === i;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center text-center min-w-[60px]">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                done ? "bg-[#205c45] text-white" : "bg-[#e6ebe8] text-[#8ba397]"
              } ${active ? "ring-2 ring-[#205c45] ring-offset-1" : ""}`}>{i + 1}</div>
              <span className={`text-[9px] uppercase tracking-wider mt-1 ${done ? "text-[#205c45] font-semibold" : "text-[#8ba397]"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 ${current > i && !isRech && !isCorr ? "bg-[#205c45]" : "bg-[#e6ebe8]"}`} />
            )}
          </React.Fragment>
        );
      })}
      {isRech && (
        <div className="ml-2 px-2 py-0.5 rounded bg-[#fdecea] text-[#b53a25] text-[10px] uppercase font-bold tracking-wider">Rechazado</div>
      )}
      {isCorr && (
        <div className="ml-2 px-2 py-0.5 rounded bg-[#fef3e7] text-[#a5560c] border border-[#f0b874] text-[10px] uppercase font-bold tracking-wider">Requiere Correcciones</div>
      )}
    </div>
  );
}

export default function LiquidacionesViaticos() {
  const { user } = useAuth();
  const { highlightId, setRef, flash } = useRowHighlight();
  const isVenTodas = ROLES_VEN_TODAS.includes(user?.role);
  const isRevisor = ["admin", "gerencia"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filterEstado, setFilterEstado] = useState("");
  const [verTodos, setVerTodos] = useState(isVenTodas);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [editingEstado, setEditingEstado] = useState(null);
  const [revDialog, setRevDialog] = useState(null);
  const [busquedaIdx, setBusquedaIdx] = useState(null); // índice de línea en proceso de búsqueda

  function empty() {
    return {
      tipo_actividad: "COMBUSTIBLE MES",
      ciudad: "Guayaquil",
      fecha: new Date().toISOString().slice(0, 10),
      comentario: "",
      lineas: [emptyLinea()],
    };
  }
  function emptyLinea() {
    return { concepto: "COMBUSTIBLE", numero_comprobante: "", razon_social: "", ruc: "", valor: 0 };
  }

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filterEstado) params.set("estado", filterEstado);
      if (verTodos && isVenTodas) params.set("todos", "true");
      const [{ data }, { data: c }, catRes] = await Promise.all([
        api.get(`/liquidaciones-viaticos?${params}`),
        api.get("/liquidaciones-viaticos/conceptos/sugeridos"),
        api.get("/gastos/categorias").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(data) ? data : []); setConceptos(Array.isArray(c) ? c : []); setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterEstado, verTodos]);

  const total = useMemo(() => form.lineas.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0), [form.lineas]);

  const updateLinea = (i, field, val) => {
    setForm(f => {
      const n = [...f.lineas]; n[i] = { ...n[i], [field]: val }; return { ...f, lineas: n };
    });
  };
  const addLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, emptyLinea()] }));
  const removeLinea = (i) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, x) => x !== i) }));

  // Búsqueda por número de comprobante
  const buscarComprobante = async (idx) => {
    const numero = (form.lineas[idx]?.numero_comprobante || "").trim();
    if (!numero) return toast.error("Ingrese un número de comprobante para buscar");
    setBusquedaIdx(idx);
    try {
      const { data } = await api.get(`/liquidaciones-viaticos/buscar-comprobante?numero=${encodeURIComponent(numero)}`);
      if (!data.encontrado) {
        toast.error("No se encontró ningún comprobante con ese número");
      } else {
        setForm(f => {
          const n = [...f.lineas];
          n[idx] = {
            ...n[idx],
            razon_social: data.razon_social || n[idx].razon_social,
            ruc: data.ruc || n[idx].ruc,
            valor: data.total ?? n[idx].valor,
          };
          return { ...f, lineas: n };
        });
        const fuenteLabel = { factura_compra: "Factura de compra", gasto_operativo: "Gasto operativo previo", liquidacion_anterior: "Liquidación anterior" }[data.fuente] || "encontrado";
        const retencionMsg = data.retencion > 0 ? ` (incluye retención $${data.retencion.toFixed(2)} descontada del bruto $${data.total_bruto.toFixed(2)})` : "";
        toast.success(`${fuenteLabel}: ${data.razon_social || "—"} · $${(data.total || 0).toFixed(2)}${retencionMsg}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusquedaIdx(null);
    }
  };

  // ====== Sugerencias proveedor / comprobante (typeahead) ======
  const [provSug, setProvSug] = useState({}); // {idx: [{id, razon_social, ruc}]}
  const [compSug, setCompSug] = useState({}); // {idx: [{numero_comprobante, total, ...}]}
  const [openSug, setOpenSug] = useState({}); // {idx: "razon"|"comp"|null}

  const buscarProveedores = async (idx, q) => {
    try {
      const { data } = await api.get(`/liquidaciones-viaticos/proveedores-sugeridos?q=${encodeURIComponent(q || "")}`);
      setProvSug(s => ({ ...s, [idx]: data }));
    } catch (e) {
      // silencioso
    }
  };
  const buscarComprobantesSug = async (idx, q) => {
    const ruc = (form.lineas[idx]?.ruc || "").trim();
    if (!ruc) {
      setCompSug(s => ({ ...s, [idx]: [] }));
      return;
    }
    try {
      const { data } = await api.get(`/liquidaciones-viaticos/comprobantes-sugeridos?ruc=${encodeURIComponent(ruc)}&q=${encodeURIComponent(q || "")}`);
      setCompSug(s => ({ ...s, [idx]: data }));
    } catch (e) {
      // silencioso
    }
  };
  const seleccionarProveedor = (idx, p) => {
    setForm(f => {
      const n = [...f.lineas];
      n[idx] = { ...n[idx], razon_social: p.razon_social, ruc: p.ruc };
      return { ...f, lineas: n };
    });
    setOpenSug(s => ({ ...s, [idx]: null }));
    setCompSug(s => ({ ...s, [idx]: [] }));
    // Pre-cargar comprobantes del proveedor
    setTimeout(() => buscarComprobantesSug(idx, ""), 50);
  };
  const seleccionarComprobante = (idx, c) => {
    setForm(f => {
      const n = [...f.lineas];
      n[idx] = {
        ...n[idx],
        numero_comprobante: c.numero_comprobante,
        razon_social: c.razon_social || n[idx].razon_social,
        ruc: c.ruc || n[idx].ruc,
        valor: c.total ?? n[idx].valor,
      };
      return { ...f, lineas: n };
    });
    setOpenSug(s => ({ ...s, [idx]: null }));
    const ret = c.retencion > 0 ? ` (− retención $${c.retencion.toFixed(2)})` : "";
    toast.success(`${c.numero_comprobante} · $${(c.total || 0).toFixed(2)}${ret}`);
  };

  const save = async ({ reenviar = false } = {}) => {
    if (!form.tipo_actividad) return toast.error("Indique el tipo de actividad");
    if (form.lineas.length === 0) return toast.error("Agregue al menos una línea");
    const lineasClean = form.lineas
      .filter(l => parseFloat(l.valor) > 0)
      .map(l => ({ ...l, valor: parseFloat(l.valor) }));
    if (lineasClean.length === 0) return toast.error("Las líneas deben tener valor > 0");
    const payload = { ...form, lineas: lineasClean };
    try {
      let savedId = null;
      if (editingId) {
        const res = await api.patch(`/liquidaciones-viaticos/${editingId}`, payload);
        savedId = res?.data?.id || editingId;
        toast.success("Liquidación actualizada");
      } else {
        const res = await api.post("/liquidaciones-viaticos", payload);
        savedId = res?.data?.id;
        toast.success("Liquidación creada (borrador)");
      }
      if (reenviar && savedId) {
        try {
          await api.post(`/liquidaciones-viaticos/${savedId}/enviar`);
          toast.success("Enviada nuevamente a revisión");
        } catch (e) {
          toast.error(`Guardada, pero no se pudo reenviar: ${formatApiError(e)}`);
        }
      }
      setDialogOpen(false); setEditingId(null); setEditingEstado(null); setForm(empty());
      await load();
      if (savedId) flash(savedId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editar = (it) => {
    setEditingId(it.id);
    setEditingEstado(it.estado);
    setForm({
      tipo_actividad: it.tipo_actividad, ciudad: it.ciudad,
      fecha: it.fecha, comentario: it.comentario || "",
      lineas: it.lineas?.length ? it.lineas : [emptyLinea()],
    });
    setDialogOpen(true);
  };

  const enviarRevision = async (it) => {
    if (!window.confirm(`¿Enviar liquidación ${it.numero} a revisión?`)) return;
    try { await api.post(`/liquidaciones-viaticos/${it.id}/enviar`); toast.success("Enviada a revisión"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminar = async (it) => {
    if (!window.confirm(`¿Eliminar liquidación ${it.numero}?`)) return;
    try { await api.delete(`/liquidaciones-viaticos/${it.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const verPdf = async (it) => {
    try {
      const r = await api.get(`/liquidaciones-viaticos/${it.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="liqv-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Recursos Humanos</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Liquidación de Viáticos
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">Reporte de gastos del colaborador (combustible, peajes, viajes, atención cliente). Genera PDF F-DDHH-LIQV-002 para reembolso.</p>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Estado</Label>
          <Select value={filterEstado || "all"} onValueChange={(v) => setFilterEstado(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="liqv-estado-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isVenTodas && (
          <div className="flex items-center gap-2 mb-1">
            <input type="checkbox" id="vertodos" checked={verTodos} onChange={(e) => setVerTodos(e.target.checked)} data-testid="liqv-ver-todos" />
            <Label htmlFor="vertodos" className="text-xs">Ver todas (no solo las mías)</Label>
          </div>
        )}
        <div className="ml-auto">
          <Button onClick={() => { setEditingId(null); setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-liqv-btn">
            <Plus size={16} className="mr-2" /> Nueva liquidación
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Nº</th><th>Tipo actividad</th><th>Colaborador</th><th>Fecha</th><th className="text-right">Total</th><th>Estado</th><th>Revisor</th><th className="w-44 text-right">Acciones</th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} ref={setRef(it.id)} className={highlightId === it.id ? "row-highlight" : ""} data-testid={`liqv-row-${it.id}`}>
                <td className="font-mono text-xs">{it.numero}</td>
                <td className="font-medium">{it.tipo_actividad}</td>
                <td>{it.colaborador_nombre}</td>
                <td className="text-xs">{it.fecha}</td>
                <td className="text-right tabular-nums font-semibold">${(it.total || 0).toFixed(2)}</td>
                <td><Badge className={ESTADOS[it.estado]?.cls}>{ESTADOS[it.estado]?.label || it.estado}</Badge></td>
                <td className="text-xs">{it.revisor?.nombre || "—"}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    {["aprobado", "reembolsado", "rechazado"].includes(it.estado) && (
                      <Button size="icon" variant="ghost" title="Descargar PDF" onClick={() => verPdf(it)} data-testid={`liqv-pdf-${it.id}`}><Download size={14} /></Button>
                    )}
                    <Button size="icon" variant="ghost" title="Ver detalle" onClick={() => setViewing(it)}><Eye size={14} /></Button>
                    {(it.estado === "borrador" || it.estado === "rechazado" || it.estado === "requiere_correccion") && it.colaborador_id === user?.id && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => editar(it)} title="Editar" data-testid={`liqv-editar-${it.id}`}><Edit2 size={14} /></Button>
                        {(it.estado === "requiere_correccion" || it.estado === "rechazado") ? (
                          <Button size="sm" className="bg-[#205c45] text-white hover:bg-[#15241d] h-8 px-2" onClick={() => enviarRevision(it)} title="Reenviar a revisión" data-testid={`liqv-reenviar-${it.id}`}>
                            <Send size={12} className="mr-1" /> Reenviar
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" onClick={() => enviarRevision(it)} title="Enviar a revisión" data-testid={`liqv-enviar-${it.id}`}><Send size={14} className="text-[#205c45]" /></Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => eliminar(it)}><Trash2 size={14} className="text-[#b53a25]" /></Button>
                      </>
                    )}
                    {isRevisor && it.estado === "enviado" && (
                      <Button size="sm" variant="outline" onClick={() => setRevDialog({ ...it, accion: "aprobar" })} data-testid={`liqv-revisar-${it.id}`}>Revisar</Button>
                    )}
                    {isRevisor && it.estado === "aprobado" && (
                      <Button size="sm" variant="outline" className="bg-[#205c45] text-white hover:bg-[#15241d]" onClick={() => setRevDialog({ ...it, accion: "reembolsar" })}>Reembolsar</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin liquidaciones</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Crear/editar */}
      <Sheet open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditingId(null); setEditingEstado(null); } }}>
        <SheetContent side="right" className="w-full sm:!max-w-[1100px] flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#d1dbd6]">
            <SheetTitle>{editingId ? "Editar liquidación" : "Nueva liquidación de viáticos"}</SheetTitle>
            <SheetDescription>Registre conceptos de gasto del colaborador. Al guardar quedará en estado borrador hasta que la envíe a revisión.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Tipo de Actividad</Label><Input value={form.tipo_actividad} onChange={(e) => setForm({ ...form, tipo_actividad: e.target.value })} placeholder="Ej: COMBUSTIBLE MES MAYO" data-testid="liqv-tipo" /></div>
            <div><Label>Ciudad</Label><Input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="liqv-fecha" /></div>
            <div className="col-span-3"><Label>Breve comentario de las actividades realizadas</Label><Textarea rows={2} value={form.comentario} onChange={(e) => setForm({ ...form, comentario: e.target.value })} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Líneas de gasto</div>
              <Button size="sm" variant="outline" onClick={addLinea}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Concepto</th><th className="min-w-[200px]">Razón Social</th><th className="min-w-[140px]">RUC</th><th className="min-w-[180px]">Nº Comprobante</th><th className="w-28 text-right">Valor</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.lineas.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <Input list={`conceptos-${i}`} value={l.concepto} onChange={(e) => updateLinea(i, "concepto", e.target.value)} className="h-9" data-testid={`liqv-linea-concepto-${i}`} />
                      <datalist id={`conceptos-${i}`}>{conceptos.map((c) => <option key={c} value={c} />)}</datalist>
                    </td>
                    <td>
                      <div className="relative">
                        <Input
                          value={l.razon_social}
                          onChange={(e) => { updateLinea(i, "razon_social", e.target.value); buscarProveedores(i, e.target.value); setOpenSug(s => ({ ...s, [i]: "razon" })); }}
                          onFocus={() => { buscarProveedores(i, l.razon_social || ""); setOpenSug(s => ({ ...s, [i]: "razon" })); }}
                          onBlur={() => setTimeout(() => setOpenSug(s => ({ ...s, [i]: null })), 200)}
                          className="h-9"
                          placeholder="Empieza a escribir…"
                          data-testid={`liqv-linea-razon-${i}`}
                        />
                        {openSug[i] === "razon" && (provSug[i] || []).length > 0 && (
                          <div className="absolute z-50 mt-1 w-[320px] max-h-72 overflow-y-auto bg-white border border-[#d1dbd6] rounded shadow-lg" data-testid={`liqv-linea-razon-dropdown-${i}`}>
                            {(provSug[i] || []).map((p) => (
                              <button
                                type="button"
                                key={p.id}
                                onMouseDown={(e) => { e.preventDefault(); seleccionarProveedor(i, p); }}
                                className="w-full text-left px-3 py-2 hover:bg-[#f3f7f5] border-b border-[#eef0ee] last:border-0"
                              >
                                <div className="text-sm font-medium text-[#15241d] truncate">{p.razon_social}</div>
                                <div className="text-[11px] font-mono text-[#5c7368]">{p.ruc}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td><Input value={l.ruc} onChange={(e) => updateLinea(i, "ruc", e.target.value)} className="h-9 font-mono text-xs" placeholder="Auto" /></td>
                    <td>
                      <div className="relative">
                        <div className="flex gap-1">
                          <Input
                            value={l.numero_comprobante}
                            onChange={(e) => { updateLinea(i, "numero_comprobante", e.target.value); buscarComprobantesSug(i, e.target.value); setOpenSug(s => ({ ...s, [i]: "comp" })); }}
                            onFocus={() => { buscarComprobantesSug(i, l.numero_comprobante || ""); setOpenSug(s => ({ ...s, [i]: "comp" })); }}
                            onBlur={() => setTimeout(() => setOpenSug(s => ({ ...s, [i]: null })), 200)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarComprobante(i); } }}
                            className="h-9 font-mono text-xs"
                            placeholder={l.ruc ? "001-001-..." : "Selecciona razón social primero"}
                            disabled={!l.ruc}
                            data-testid={`liqv-linea-comprobante-${i}`}
                          />
                          <Button type="button" size="icon" variant="outline" onClick={() => buscarComprobante(i)} disabled={busquedaIdx === i || !l.numero_comprobante} title="Buscar y autocompletar datos" data-testid={`liqv-buscar-${i}`}>
                            <Search size={14} />
                          </Button>
                        </div>
                        {openSug[i] === "comp" && (compSug[i] || []).length > 0 && (
                          <div className="absolute z-50 mt-1 w-[340px] max-h-72 overflow-y-auto bg-white border border-[#d1dbd6] rounded shadow-lg right-0" data-testid={`liqv-linea-comprobante-dropdown-${i}`}>
                            {(compSug[i] || []).map((c, ci) => (
                              <button
                                type="button"
                                key={`${c.numero_comprobante}-${ci}`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  if (c.ya_reembolsado) {
                                    const liq = c.liquidacion_previa || {};
                                    if (!window.confirm(
                                      `⚠️ Este comprobante YA fue reembolsado en la liquidación ${liq.liquidacion_numero || ""} (${liq.fecha || ""}, estado: ${liq.estado || ""}).\n\n¿Está seguro que desea volver a registrarlo?`
                                    )) return;
                                  }
                                  seleccionarComprobante(i, c);
                                }}
                                className={`w-full text-left px-3 py-2 hover:bg-[#f3f7f5] border-b border-[#eef0ee] last:border-0 grid grid-cols-[1fr_auto] gap-2 items-center ${c.ya_reembolsado ? "bg-[#fef4f2] hover:bg-[#fde9e4]" : ""}`}
                              >
                                <div>
                                  <div className="font-mono text-xs text-[#15241d] flex items-center gap-2">
                                    {c.numero_comprobante}
                                    {c.ya_reembolsado && (
                                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#b53a25] text-white font-bold" data-testid={`ya-reembolsado-${i}-${ci}`}>
                                        Ya reembolsado
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] uppercase tracking-wide text-[#8ba397]">
                                    {c.fuente === "factura_compra" ? "Factura compra" : c.fuente === "gasto_operativo" ? "Gasto operativo" : c.fuente}
                                    {c.fecha ? ` · ${String(c.fecha).slice(0,10)}` : ""}
                                    {c.ya_reembolsado && c.liquidacion_previa ? ` · en ${c.liquidacion_previa.liquidacion_numero}` : ""}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className={`text-sm font-semibold tabular-nums ${c.ya_reembolsado ? "text-[#b53a25] line-through" : "text-[#205c45]"}`}>${(c.total || 0).toFixed(2)}</div>
                                  {c.retencion > 0 && <div className="text-[10px] text-[#8ba397]">−ret ${c.retencion.toFixed(2)}</div>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td><Input type="number" step="0.01" value={l.valor} onChange={(e) => updateLinea(i, "valor", e.target.value)} className="h-9 text-right tabular-nums" /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => removeLinea(i)}><Trash2 size={14} className="text-[#b53a25]" /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#f7f9f8] border-t">
                <tr><td colSpan={5} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">TOTAL &gt;&gt;</td>
                    <td className="text-right font-bold text-lg tabular-nums px-3 py-2" data-testid="liqv-total">${total.toFixed(2)}</td></tr>
              </tfoot>
            </table>
          </div>
          </div>
          <SheetFooter className="border-t border-[#d1dbd6] px-6 py-4 gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => save()} variant="outline" className="border-[#205c45] text-[#205c45] hover:bg-[#e9f2ee]" data-testid="save-liqv-btn">
              {editingEstado === "requiere_correccion" || editingEstado === "rechazado" ? "Solo guardar" : "Guardar borrador"}
            </Button>
            {(editingEstado === "requiere_correccion" || editingEstado === "rechazado") && (
              <Button onClick={() => save({ reenviar: true })} className="bg-[#205c45] hover:bg-[#15241d]" data-testid="save-and-resend-liqv-btn">
                <Send size={14} className="mr-1" /> Guardar y reenviar a revisión
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Detalle */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle><FileText className="inline mr-2" size={18} />Liquidación {viewing?.numero}</DialogTitle>
            <DialogDescription>Detalle del reporte de gastos · ciclo de aprobación y líneas registradas</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
              {/* Timeline visual */}
              <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3">
                <Timeline estado={viewing.estado} />
              </div>
              {viewing.estado === "requiere_correccion" && viewing.motivo_revision && (
                <div className="rounded-md border border-[#f0b874] bg-[#fef7ec] p-3" data-testid="liqv-motivo-correccion">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[#8a5500] mb-1">Correcciones solicitadas por {viewing.revisor?.nombre || "revisor"}</div>
                  <div className="text-sm text-[#5a3a05]">{viewing.motivo_revision}</div>
                </div>
              )}
              {viewing.estado === "rechazado" && viewing.motivo_revision && (
                <div className="rounded-md border border-[#d99584] bg-[#fdecea] p-3">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[#b53a25] mb-1">Motivo de rechazo</div>
                  <div className="text-sm text-[#7a2312]">{viewing.motivo_revision}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Tipo Actividad</Label><div>{viewing.tipo_actividad}</div></div>
                <div><Label className="text-xs">Estado</Label><div><Badge className={ESTADOS[viewing.estado]?.cls}>{ESTADOS[viewing.estado]?.label}</Badge></div></div>
                <div><Label className="text-xs">Colaborador</Label><div>{viewing.colaborador_nombre}</div></div>
                <div><Label className="text-xs">Fecha</Label><div>{viewing.fecha}</div></div>
                <div className="col-span-2"><Label className="text-xs">Comentario</Label><div className="text-[#4a5c53]">{viewing.comentario || "—"}</div></div>
              </div>
              <table className="erp-table">
                <thead><tr><th>Concepto</th><th>Comprob.</th><th>Razón social</th><th>RUC</th><th className="text-right">Valor</th></tr></thead>
                <tbody>
                  {viewing.lineas?.map((l, i) => (
                    <tr key={i}>
                      <td>{l.concepto}</td><td className="font-mono text-xs">{l.numero_comprobante}</td>
                      <td>{l.razon_social}</td><td className="font-mono text-xs">{l.ruc}</td>
                      <td className="text-right tabular-nums">${(parseFloat(l.valor)||0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={4} className="text-right font-semibold">Total</td><td className="text-right tabular-nums font-bold">${(viewing.total||0).toFixed(2)}</td></tr></tfoot>
              </table>
              {viewing.history?.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-xs text-[#5c7368] uppercase tracking-wider font-semibold">Historial</summary>
                  <ul className="mt-2 text-xs space-y-1">
                    {viewing.history.map((h, i) => (
                      <li key={i} className="border-l-2 border-[#205c45] pl-2 py-1"><strong>{h.accion}</strong> · {h.user_nombre} · {(h.fecha||"").slice(0,16).replace("T"," ")} {h.motivo && `· ${h.motivo}`}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revisión gerencia */}
      <RevisionDialog item={revDialog} setItem={setRevDialog} onDone={load} categorias={categorias} />
    </div>
  );
}

function RevisionDialog({ item, setItem, onDone, categorias }) {
  const [accion, setAccion] = useState("aprobar");
  const [motivo, setMotivo] = useState("");
  const [crearGasto, setCrearGasto] = useState(true);
  const [categoriaId, setCategoriaId] = useState("");
  const [medio, setMedio] = useState("transferencia");

  useEffect(() => {
    if (item) { setAccion(item.accion || "aprobar"); setMotivo(""); setCategoriaId(""); }
  }, [item]);

  const close = React.useCallback(() => setItem(null), [setItem]);

  if (!item) return null;

  const submit = async () => {
    try {
      const body = { accion, motivo: motivo || null };
      if (accion === "rechazar" && !motivo.trim()) return toast.error("Ingrese el motivo del rechazo");
      if (accion === "corregir" && !motivo.trim()) return toast.error("Indique qué debe corregir el colaborador");
      if (accion === "reembolsar") {
        body.crear_gasto_operativo = crearGasto;
        body.categoria_id = crearGasto ? categoriaId : null;
        body.medio_pago = medio;
        if (crearGasto && !categoriaId) return toast.error("Seleccione categoría");
      }
      await api.post(`/liquidaciones-viaticos/${item.id}/revision`, body);
      const msg = {
        aprobar: "aprobada",
        rechazar: "rechazada",
        corregir: "enviada al colaborador para correcciones",
        reembolsar: "reembolsada",
      }[accion] || "actualizada";
      toast.success(`Liquidación ${msg}`);
      close(); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="liqv-revision-dialog">
        <DialogHeader>
          <DialogTitle>Revisar {item.numero} — ${(item.total||0).toFixed(2)}</DialogTitle>
          <DialogDescription>Aprobar, rechazar o reembolsar la liquidación. El colaborador será notificado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Acción</Label>
            <Select value={accion} onValueChange={setAccion}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {item.estado === "enviado" && <>
                  <SelectItem value="aprobar">Aprobar</SelectItem>
                  <SelectItem value="corregir">Solicitar correcciones</SelectItem>
                  <SelectItem value="rechazar">Rechazar</SelectItem>
                </>}
                {item.estado === "aprobado" && <SelectItem value="reembolsar">Reembolsar</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          {accion === "rechazar" && <div><Label>Motivo *</Label><Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} required data-testid="liqv-revision-motivo-rechazar" /></div>}
          {accion === "corregir" && (
            <div className="rounded-md border border-[#f0b874] bg-[#fef7ec] p-3">
              <Label className="text-[#8a5500]">Indique qué debe corregir el colaborador *</Label>
              <Textarea
                rows={3}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                required
                placeholder="Ej: Falta adjuntar comprobante del peaje; el monto de combustible no coincide con la factura."
                data-testid="liqv-revision-motivo-corregir"
                className="mt-1"
              />
              <p className="text-[11px] text-[#8a5500] mt-2">La liquidación quedará en estado <strong>Requiere Correcciones</strong>; el colaborador podrá editarla y reenviarla a revisión.</p>
            </div>
          )}
          {accion === "reembolsar" && (
            <>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="crear-gasto" checked={crearGasto} onChange={(e) => setCrearGasto(e.target.checked)} />
                <Label htmlFor="crear-gasto" className="text-xs">Crear gasto operativo automáticamente (con asiento contable)</Label>
              </div>
              {crearGasto && (
                <>
                  <div>
                    <Label>Categoría de gasto</Label>
                    <Select value={categoriaId} onValueChange={setCategoriaId}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Medio de pago del reembolso</Label>
                    <Select value={medio} onValueChange={setMedio}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="caja_chica">Caja Chica</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancelar</Button>
          <Button onClick={submit} className={accion === "rechazar" ? "bg-[#b53a25]" : accion === "corregir" ? "bg-[#a5560c]" : "bg-[#205c45]"} data-testid="liqv-revision-confirm">
            {accion === "aprobar" && <Check size={14} className="mr-1" />}
            {accion === "rechazar" && <X size={14} className="mr-1" />}
            {accion === "corregir" && <Edit2 size={14} className="mr-1" />}
            {accion === "reembolsar" && <RefreshCw size={14} className="mr-1" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
